// src/lib/api/financial/incomeCategories.js
import { supabase } from '../../supabase'
import { getValidatedCompanyId } from '../shared/helpers'
import { withErrorHandling, createSuccessResponse } from '../shared/errors'
import { validateRequired, sanitizeInput } from '../shared/validation'

const DEFAULT_INCOME_CATEGORIES = [
  { name: 'Pressure Washing', description: 'Revenue from pressure washing services', is_system: true },
  { name: 'Soft Washing', description: 'Revenue from soft washing services', is_system: true },
  { name: 'Window Cleaning', description: 'Revenue from window cleaning services', is_system: true },
  { name: 'Gutter Cleaning', description: 'Revenue from gutter cleaning services', is_system: true },
  { name: 'Surface Cleaning', description: 'Revenue from surface cleaning services', is_system: true },
  { name: 'Other Services', description: 'Revenue from other services', is_system: true },
  { name: 'Tips', description: 'Customer tips and gratuities', is_system: true },
  { name: 'Refunds Given', description: 'Refunds issued to customers (negative income)', is_system: true }
]

/**
 * Seeds default income categories if none exist for the company.
 * Called on first access.
 */
const _seedDefaultIncomeCategories = async () => {
  const companyId = await getValidatedCompanyId()
  if (!companyId) return createSuccessResponse([])

  // Check if categories already exist
  const { data: existing, error: checkError } = await supabase
    .from('income_categories')
    .select('id')
    .eq('company_id', companyId)
    .limit(1)

  if (checkError) throw checkError

  if (existing && existing.length > 0) {
    // Categories already exist, just return them
    const { data, error } = await supabase
      .from('income_categories')
      .select('*')
      .eq('company_id', companyId)
      .order('name', { ascending: true })

    if (error) throw error
    return createSuccessResponse(data || [])
  }

  // Seed defaults
  const rows = DEFAULT_INCOME_CATEGORIES.map(cat => ({
    company_id: companyId,
    name: cat.name,
    description: cat.description,
    is_system: cat.is_system
  }))

  const { data, error } = await supabase
    .from('income_categories')
    .insert(rows)
    .select()

  if (error) throw error
  return createSuccessResponse(data || [])
}

/**
 * List income categories. Seeds defaults on first access.
 */
const _listIncomeCategories = async () => {
  return _seedDefaultIncomeCategories()
}

/**
 * Create a custom income category
 */
const _createIncomeCategory = async (categoryData) => {
  validateRequired(categoryData.name, 'Category name')

  const companyId = await getValidatedCompanyId()
  if (!companyId) throw new Error('No company ID found')

  const { data, error } = await supabase
    .from('income_categories')
    .insert([{
      company_id: companyId,
      name: sanitizeInput(categoryData.name),
      description: categoryData.description ? sanitizeInput(categoryData.description) : null,
      is_system: false
    }])
    .select()
    .single()

  if (error) throw error
  return data
}

/**
 * Update an income category
 */
const _updateIncomeCategory = async (id, categoryData) => {
  validateRequired(id, 'Category ID')

  const companyId = await getValidatedCompanyId()
  if (!companyId) throw new Error('No company ID found')

  const updatePayload = {}
  if (categoryData.name) updatePayload.name = sanitizeInput(categoryData.name)
  if (categoryData.description !== undefined) updatePayload.description = categoryData.description ? sanitizeInput(categoryData.description) : null

  const { data, error } = await supabase
    .from('income_categories')
    .update(updatePayload)
    .eq('id', id)
    .eq('company_id', companyId)
    .select()
    .single()

  if (error) throw error
  return data
}

/**
 * Delete a custom income category (cannot delete system categories)
 */
const _deleteIncomeCategory = async (id) => {
  validateRequired(id, 'Category ID')

  const companyId = await getValidatedCompanyId()
  if (!companyId) throw new Error('No company ID found')

  // Only delete non-system categories
  const { error } = await supabase
    .from('income_categories')
    .delete()
    .eq('id', id)
    .eq('company_id', companyId)
    .eq('is_system', false)

  if (error) throw error
  return { success: true }
}

// Export wrapped functions
export const listIncomeCategories = withErrorHandling(_listIncomeCategories, 'list income categories', 'income_categories')
export const seedDefaultIncomeCategories = withErrorHandling(_seedDefaultIncomeCategories, 'seed income categories', 'income_categories')
export const createIncomeCategory = withErrorHandling(_createIncomeCategory, 'create income category', 'income_category')
export const updateIncomeCategory = withErrorHandling(_updateIncomeCategory, 'update income category', 'income_category')
export const deleteIncomeCategory = withErrorHandling(_deleteIncomeCategory, 'delete income category', 'income_category')
